import clsx from "clsx";
import { isAllDay } from "common";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Event, Experience, Permission, Timeslot } from "types";
import shallow from "zustand/shallow";
import { EventApi } from "@fullcalendar/common";
import { IsGranted } from "../../Common/IsGranted";
import { getCalendarColors } from "../Colors/colorHelpers";
import { BalanceBadge } from "../Common/BalanceBadge";
import { BlackoutTimeslotTooltip } from "../Common/BlackoutTimeslotTooltip";
import { normalizeTimeslotId } from "./DashboardCalendar";
import {
    CalendarItemTooltipHeading,
    CheckedInAndVouchersGrouped,
    DashboardCalendarItemBadgeContent,
    DashboardCalendarItemTitle,
    SubInfoGrouped,
} from "./DashboardCalendarItemResponsiveComponents";
import {
    shouldShowBalance,
    shouldShowBalanceInFirstRow,
    shouldShowBalanceTooltip,
    shouldShowCheckedInAndVouchersSeparate,
    shouldShowExperienceInLastRow,
    shouldShowMainInfo,
    shouldShowName,
    shouldShowReservedCount,
    shouldShowSubInfoSeparate,
} from "./dashboardResponsiveHelpers";
import { canOpenRoster } from "@/models/Timeslot";
import { useRosterDrawerStore } from "@/stores/rosterDrawerStore";
import "./timegrid.css";

interface DashboardCalendarItemContentProps {
    event: EventApi;
    toggleEventSelection: (event: EventApi) => void;
}

export const DashboardCalendarItemContent = ({
    event: fcEvent,
    toggleEventSelection,
}: DashboardCalendarItemContentProps) => {
    const componentRef = useRef<HTMLDivElement>(null);
    const outerParentClassList = (componentRef.current?.parentNode?.parentNode as HTMLElement)?.classList;
    const isEndOfMultiDayEvent =
        !outerParentClassList?.contains("fc-event-start") && outerParentClassList?.contains("fc-event-end");

    const previousWidth = useRef(0);
    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);

    const {
        title,
        extendedProps: { timeslot, experience, event },
    } = fcEvent;
    const { aggregatedBalance } = timeslot;

    const [setDrawerOpen, setDrawerTimeslot] = useRosterDrawerStore(
        (state) => [state.setOpen, state.setTimeslot],
        shallow,
    );

    const styles = useMemo(
        () => getCalendarColors(timeslot, experience?.id, isEndOfMultiDayEvent),
        [timeslot, experience?.id, isEndOfMultiDayEvent],
    );

    const isRosterAvailable = canOpenRoster(timeslot);

    const handleEventTileClick = useCallback(
        (e) => {
            if (e.ctrlKey || e.metaKey) {
                toggleEventSelection(fcEvent);
                return;
            }

            if (!isRosterAvailable) {
                return;
            }

            setDrawerTimeslot(timeslot);
            setDrawerOpen();
        },
        [fcEvent, isRosterAvailable, setDrawerOpen, setDrawerTimeslot, timeslot, toggleEventSelection],
    );

    useEffect(() => {
        const updateComponentSize = () => {
            if (componentRef.current) {
                const { clientWidth, clientHeight } = componentRef.current;
                if (clientWidth !== previousWidth.current) {
                    setWidth(clientWidth);
                    previousWidth.current = clientWidth;
                }

                setHeight(clientHeight);
            }
        };

        // Call the function to update size initially
        updateComponentSize();

        // Update size on window resize
        window.addEventListener("resize", updateComponentSize);

        return () => {
            window.removeEventListener("resize", updateComponentSize);
        };
    }, [width]);

    const blockedReason = isRosterAvailable ? undefined : timeslot.unavailability?.message;

    const tile = (
        // Append to body to not inherit FC's styles and stay over FC

        <div
            ref={componentRef}
            id={normalizeTimeslotId(timeslot.id)}
            className={clsx(
                "flex h-full !max-w-[100%] rounded p-1 text-sm text-black",
                isRosterAvailable ? "hover:cursor-pointer" : "hover:cursor-not-allowed",
                fcEvent.classNames.includes("selected") && "!bg-blue !text-white",
            )}
            style={styles}
            onClick={handleEventTileClick}
        >
            <div className="flex h-fit w-full flex-col items-start space-y-2">
                {/* First Row with Timeslot info */}
                <div className="flex h-fit w-full items-center space-x-1">
                    <DashboardCalendarItemTitle
                        fcEvent={fcEvent}
                        width={width}
                        height={height}
                        isMultiDayEvent={isEndOfMultiDayEvent}
                    />
                    <DashboardCalendarItemDetails
                        experience={experience}
                        fcEvent={fcEvent}
                        width={width}
                        height={height}
                    />
                </div>

                {/* Second Row with Timeslot info */}
                {shouldShowSubInfoSeparate(height, width) && <SubInfoGrouped event={event} timeslot={timeslot} />}

                {shouldShowCheckedInAndVouchersSeparate(height, width) && (
                    <CheckedInAndVouchersGrouped timeslot={timeslot} experience={experience} />
                )}

                {/* Third Row with Timeslot info */}
                {shouldShowExperienceInLastRow(height) && shouldShowName(height, width) && (
                    <span className="mx-2 w-full overflow-hidden text-ellipsis text-xs font-bold">{title}</span>
                )}

                {aggregatedBalance > 0 && (
                    <IsGranted permissions={Permission.AMOUNT_DUE_VIEW}>
                        {!shouldShowBalanceInFirstRow(height) && shouldShowBalance(width) && (
                            <div className="flex w-full justify-end">
                                <BalanceBadge
                                    className="max-w-fit"
                                    value={aggregatedBalance}
                                    showValueInTooltip={shouldShowBalanceTooltip(height, width)}
                                />
                            </div>
                        )}
                    </IsGranted>
                )}
            </div>
        </div>
    );

    const tooltipHeading = (
        <CalendarItemTooltipHeading
            timeslot={timeslot}
            title={experience?.name}
            isMultiDayEvent={isEndOfMultiDayEvent}
        />
    );

    return (
        <BlackoutTimeslotTooltip reason={blockedReason} heading={tooltipHeading}>
            {tile}
        </BlackoutTimeslotTooltip>
    );
};

interface DashboardCalendarItemDetailsProps {
    experience?: Experience;
    fcEvent: EventApi;
    width: number;
    height: number;
}

export const DashboardCalendarItemDetails = ({
    experience,
    fcEvent,
    width,
    height,
}: DashboardCalendarItemDetailsProps) => {
    const hasTimeslot = experience ? !isAllDay(experience) : false;
    const { event }: { event: Event } = fcEvent.extendedProps as any;
    const { timeslot }: { timeslot: Timeslot } = fcEvent.extendedProps as any;

    if (!shouldShowMainInfo(height, width) && !shouldShowReservedCount(width)) {
        return null;
    }

    return (
        <div className="flex flex-grow flex-wrap items-start justify-end">
            <DashboardCalendarItemBadgeContent
                event={event}
                experience={experience}
                timeslot={timeslot}
                hasTimeslot={hasTimeslot}
                width={width}
                height={height}
            />
        </div>
    );
};
